import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import LessonGate from './LessonGate';
import NextUpEnhancer from './NextUpEnhancer';
import LockCardSave from './LockCardSave';
import PrevLessonLink from './PrevLessonLink';
import type { User } from '../App';

/**
 * Wraps a preview lesson page. Behavior depends on auth state:
 *
 *  - Signed-out visitor OR static preview build (no backend):
 *    Render the preview page raw. The in-card "Save image" button on
 *    lock cards still works so marketing previews can export PNGs.
 *
 *  - Signed-in student:
 *    Wrap in <LessonGate> which enforces the server-side drip rule and
 *    then <NextUpEnhancer> wires the existing in-page "Up next" card
 *    into a real Mark complete + navigate flow. No extra footer.
 *
 * Slug is derived from the current URL (/preview/lesson/X.Y -> "X.Y").
 */
export default function GatedLesson({
  user,
  children,
}: {
  user: User | null;
  children: ReactNode;
}) {
  const location = useLocation();
  const match = location.pathname.match(/\/preview\/lesson\/(\d+\.\d+)/);
  const slug = match ? match[1] : null;

  const isStatic = import.meta.env.VITE_STATIC_PREVIEW === '1';

  if (isStatic || !user || !slug) {
    return (
      <>
        {children}
        {slug && <LockCardSave lessonSlug={slug} lessonTitle={`Lesson ${slug}`} />}
        {slug && <PrevLessonLink currentSlug={slug} />}
      </>
    );
  }

  return (
    <LessonGate slug={slug}>
      {(ctx) => (
        <>
          {children}
          <LockCardSave lessonSlug={slug} lessonTitle={`Lesson ${slug}`} />
          <NextUpEnhancer
            next={ctx.next}
            onComplete={ctx.onComplete}
            completedAt={ctx.completedAt}
          />
          <PrevLessonLink currentSlug={slug} />
        </>
      )}
    </LessonGate>
  );
}
